<template>
  <div class="hr-modal-overlay" @click.self="$emit('close')">
    <div class="hr-modal-container hr-modal-lg">
      <div class="hr-modal-header">
        <h3>{{ position.positionName || position.name || 'Designation' }}</h3>
        <button type="button" class="hr-modal-close" @click="$emit('close')">
          <i class="bx bx-x"></i>
        </button>
      </div>
      
      <div class="hr-modal-body">
        <!-- Position details -->
        <div class="view-section">
          <h4 class="view-section-title">Position Details</h4>
          <div class="view-details-grid">
            <div class="view-detail-item">
              <label>Position Code</label>
              <span>{{ position.code }}</span>
            </div>
            <div class="view-detail-item">
              <label>Department</label>
              <span>{{ position.department || 'Not assigned' }}</span>
            </div>
            <div class="view-detail-item">
              <label>Level</label>
              <span>{{ position.level || 1 }}</span>
            </div>
            <div class="view-detail-item">
              <label>Status</label>
              <span :class="'hr-badge ' + getStatusClass(position.status)">{{ position.status }}</span>
            </div>
            <div class="view-detail-item">
              <label>Min Salary</label>
              <span>₱{{ formatSalary(position.minSalary) }}</span>
            </div>
            <div class="view-detail-item">
              <label>Max Salary</label>
              <span>₱{{ formatSalary(position.maxSalary) }}</span>
            </div>
            <div class="view-detail-item">
              <label>Supervisor</label>
              <span>{{ position.reportsTo || 'None' }}</span>
            </div>
            <div class="view-detail-item">
              <label>Fill Rate</label>
              <span>{{ getFillPercentage }}% (Inpost: {{ position.currentEmployees || 0 }} / Proposed: {{ position.maxEmployees || '∞' }})</span>
            </div>
            <div class="view-detail-item">
              <label>Variance</label>
              <span>{{ varianceDisplay }}</span>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div v-if="position.description" class="view-section">
          <h4 class="view-section-title">Description</h4>
          <p class="view-description">{{ position.description }}</p>
        </div>

        <!-- Employees in this position -->
        <div class="view-section">
          <h4 class="view-section-title">Employees in this Designation ({{ employees.length }})</h4>
          <div class="view-employees-list">
            <div v-if="employees.length === 0" class="hr-empty-small">
              <i class="bx bx-user"></i>
              <p>No employees assigned to this position</p>
            </div>
            <div v-else v-for="emp in employees" :key="emp.id" class="view-employee-item">
              <div class="employee-avatar">
                <i class="bx bx-user-circle"></i>
              </div>
              <div class="employee-details">
                <h5>{{ emp.fullName }}</h5>
                <p>{{ emp.employeeId }} • {{ emp.department || 'No Department' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-modal-footer">
        <button type="button" class="hr-btn hr-btn-secondary" @click="$emit('close')">
          Close
        </button>
        <button type="button" class="hr-btn hr-btn-primary" @click="$emit('edit', position)">
          <i class="bx bx-edit"></i> Edit
        </button>
        <button type="button" class="hr-btn hr-btn-success" @click="$emit('assign', position)">
          <i class="bx bx-user-plus"></i> Assign Employee
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewPositionModal",
  props: {
    position: {
      type: Object,
      required: true
    },
    employees: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    getFillPercentage() {
      if (!this.position.maxEmployees || this.position.maxEmployees === 0) {
        return 0;
      }
      const percentage = ((this.position.currentEmployees || 0) / this.position.maxEmployees) * 100;
      return Math.min(Math.round(percentage), 100);
    },
    varianceDisplay() {
      const proposed = Number(this.position.maxEmployees) || 0;
      const inpost = Number(this.position.currentEmployees) || 0;
      return proposed - inpost;
    }
  },
  methods: {
    formatSalary(salary) {
      if (!salary) return '0';
      return Number(salary).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getStatusClass(status) {
      return status === 'active' ? 'hr-badge-success' : 'hr-badge-secondary';
    }
  }
};
</script>

<style scoped>
@import './positionModalShared.css';

.view-section { margin-bottom: 24px; }
.view-section-title { font-size: 1rem; font-weight: 600; margin: 0 0 12px 0; padding-bottom: 8px; border-bottom: 1px solid #e2e8f0; }
.view-details-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
.view-detail-item { display: flex; flex-direction: column; gap: 4px; }
.view-detail-item label { font-size: 0.8rem; color: #64748b; }
.view-detail-item span { font-size: 0.95rem; font-weight: 500; }
.view-description { color: #334155; line-height: 1.6; margin: 0; }

.view-employees-list { max-height: 250px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; }
.view-employee-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-bottom: 1px solid #e2e8f0; }
.view-employee-item:last-child { border-bottom: none; }
.employee-avatar { font-size: 28px; color: #94a3b8; }
.employee-details h5 { margin: 0; font-size: 0.9rem; }
.employee-details p { margin: 2px 0 0; font-size: 0.8rem; color: #64748b; }

.hr-empty-small { text-align: center; padding: 2rem; color: #64748b; }
.hr-empty-small i { font-size: 32px; margin-bottom: 8px; }
</style>